import { type Vector2 } from 'three';
import { type StopHandle } from '@/util/stopHandle';

/**
 * Result of projecting a pointer event into world space and detecting a hit.
 * `hit === null` means "no draggable target under the pointer".
 */
export type ProjectedPointer<H> = {
    hit: H | null;
    world: Vector2;
};

export type PointerProjection<H> = (event: PointerEvent) => ProjectedPointer<H>;

export type PointerDragOptions<H> = {
    canvas: HTMLCanvasElement;

    /**
     * Project a pointer event into world coordinates and run hit-detection.
     * Returning `{ hit: null, ... }` means "no interaction" — pointerdown is ignored,
     * and pointermove (when no drag is active) routes through `onHover`.
     */
    pointerProjection: PointerProjection<H>;

    /** Mouse button to respond to. Default 0 (left). */
    button?: number;

    /**
     * CSS pixels of pointer movement that promote a click into a drag.
     * Default 0 — drag starts immediately on pointerdown when there is a hit.
     * When > 0, a pointerdown without subsequent movement past the threshold
     * is treated as a click (calls `onClick`, never calls `onDragStart` / `onDrag`).
     */
    dragThresholdPx?: number;

    /** Called on pointermove when no drag is active. */
    onHover?: (hit: H | null, world: Vector2, event: PointerEvent) => void;

    /**
     * Called on pointerdown over a hit, before the threshold is considered —
     * for affordances that must answer the press itself (a grabbing cursor)
     * rather than promotion. Always paired with one `onPressEnd`.
     */
    onPress?: (hit: H, grabWorld: Vector2) => void;

    /**
     * Called when that press ends (pointerup / cancel / lost capture), promoted or not.
     * `release` is the projection at the release point, null on cancel / lost capture.
     * Prefer it over `pressedHit`: a drag that clamps leaves its target behind.
     *
     * Paired with each `onPress`, except on teardown — see the stop handle.
     */
    onPressEnd?: (pressedHit: H, release: ProjectedPointer<H> | null) => void;

    /** Called once when a drag begins (after the threshold check). `world` is the pointerdown position. */
    onDragStart?: (hit: H, grabWorld: Vector2) => void;

    /** Called per drag update (rAF-throttled). `world` is the current pointer position. */
    onDrag: (hit: H, world: Vector2) => void;

    /** Called when a drag ends via pointerup / pointercancel / lostpointercapture. */
    onDragEnd?: (hit: H) => void;

    /** Called on pointerup when the drag never promoted past the threshold. */
    onClick?: (hit: H, grabWorld: Vector2) => void;

    /** Called on pointerleave (e.g. for cursor / hover-state reset). */
    onPointerLeave?: () => void;

    /**
     * Toggle camera controls: `false` on drag start, `true` on drag end.
     * Optional — callers can drive this externally (e.g. via a Pinia watcher
     * on their own drag state) and omit this option entirely.
     */
    setControlsEnabled?: (enabled: boolean) => void;
};

/**
 * Generic pointer-driven drag controller for 3D scenes.
 *
 * Owns the pointer-event listeners, pointer capture, drag-state machine,
 * rAF throttling of drag updates, and (optionally) camera-controls gating.
 * The hit-test strategy and per-state callbacks are caller-supplied.
 */
export function createPointerDragController<H>(options: PointerDragOptions<H>): StopHandle {
    const {
        canvas,
        pointerProjection,
        button = 0,
        dragThresholdPx = 0,
        onHover,
        onPress,
        onPressEnd,
        onDragStart,
        onDrag,
        onDragEnd,
        onClick,
        onPointerLeave,
        setControlsEnabled,
    } = options;

    type Pending = { hit: H; grabWorld: Vector2; screenX: number; screenY: number };

    let pending: Pending | null = null;
    let active: { hit: H } | null = null;
    // Separate from `pending`/`active`: a press outlives promotion — it starts
    // on pointerdown and ends on release, whether or not a drag happened.
    let pressed: H | null = null;
    let lastWorld: Vector2 | null = null;
    let pendingFrame: number | null = null;

    const cancelScheduled = () => {
        if (pendingFrame !== null) {
            cancelAnimationFrame(pendingFrame);
            pendingFrame = null;
        }
    };

    const promote = () => {
        if (!pending) return;
        const { hit, grabWorld } = pending;
        pending = null;
        active = { hit };
        setControlsEnabled?.(false);
        onDragStart?.(hit, grabWorld);
    };

    const flushDrag = () => {
        if (!active || !lastWorld) return;
        onDrag(active.hit, lastWorld);
    };

    // Projecting runs the caller's hit-test, so call this after any end-of-drag
    // callback that rebuilds the scene.
    const endPress = (releaseEvent: PointerEvent | null) => {
        if (pressed === null) return;
        const hit = pressed;
        pressed = null;
        onPressEnd?.(hit, releaseEvent === null ? null : pointerProjection(releaseEvent));
    };

    const endActiveDrag = () => {
        if (!active) return;
        const { hit } = active;
        active = null;
        cancelScheduled();
        setControlsEnabled?.(true);
        onDragEnd?.(hit);
    };

    const onPointerDown = (event: PointerEvent) => {
        if (event.button !== button) return;
        const { hit, world } = pointerProjection(event);
        if (hit === null) return;

        event.preventDefault();
        canvas.setPointerCapture(event.pointerId);

        pending = {
            hit,
            grabWorld: world,
            screenX: event.clientX,
            screenY: event.clientY,
        };
        // A second touch replaces the press in flight; close the one it displaces so
        // every onPress keeps its pair.
        endPress(null);
        pressed = hit;
        onPress?.(hit, world);

        if (dragThresholdPx === 0) {
            promote();
        }
    };

    const onPointerMove = (event: PointerEvent) => {
        const { hit, world } = pointerProjection(event);

        if (pending) {
            const dx = event.clientX - pending.screenX;
            const dy = event.clientY - pending.screenY;
            if (dx * dx + dy * dy <= dragThresholdPx * dragThresholdPx) {
                return;
            }
            promote();
        }

        if (active) {
            lastWorld = world;
            if (pendingFrame === null) {
                pendingFrame = requestAnimationFrame(() => {
                    pendingFrame = null;
                    flushDrag();
                });
            }
            return;
        }

        onHover?.(hit, world, event);
    };

    const onPointerUp = (event: PointerEvent) => {
        if (canvas.hasPointerCapture(event.pointerId)) {
            canvas.releasePointerCapture(event.pointerId);
        }
        if (pending) {
            const { hit, grabWorld } = pending;
            pending = null;
            onClick?.(hit, grabWorld);
            endPress(event);
            return;
        }
        endActiveDrag();
        endPress(event);
    };

    const onPointerCancel = (event: PointerEvent) => {
        if (canvas.hasPointerCapture(event.pointerId)) {
            canvas.releasePointerCapture(event.pointerId);
        }
        pending = null;
        endActiveDrag();
        endPress(null);
    };

    const onLostPointerCapture = () => {
        pending = null;
        endActiveDrag();
        endPress(null);
    };

    const onLeave = () => onPointerLeave?.();

    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('pointercancel', onPointerCancel);
    canvas.addEventListener('lostpointercapture', onLostPointerCapture);
    canvas.addEventListener('pointerleave', onLeave);

    return () => {
        canvas.removeEventListener('pointerdown', onPointerDown);
        canvas.removeEventListener('pointermove', onPointerMove);
        canvas.removeEventListener('pointerup', onPointerUp);
        canvas.removeEventListener('pointercancel', onPointerCancel);
        canvas.removeEventListener('lostpointercapture', onLostPointerCapture);
        canvas.removeEventListener('pointerleave', onLeave);
        cancelScheduled();
        // Listeners are gone — leave drag state alone. Callers handle their
        // own defensive end-of-drag cleanup via the StopHandle they return
        // from their setup function.
        pending = null;
        active = null;
        pressed = null;
        lastWorld = null;
    };
}
